import {
  AVAILABLE_ORGANIZATIONS_UPDATE,
  ORGANIZATION_SELECTION_REQUIRED,
  ORGANIZATION_SELECTION_RESOLVED,
  ORGANIZATION_SELECTION_RESOLVING,
} from '.';
import organizationServiceApi from '../../api/organization.service.api';
import { DEVICE_FLAGS } from '../../constants/deviceFlags';
import { ORGANIZATION_SELECTION_STATUS } from '../../constants/organization-selection.constants';
import Logger from '../../utils/Logger';
import { updateDeviceFlags } from './deviceFlags.actions';
import { setPendingBiometricEnrollment, setSuggestBiometricEnrollment } from './auth.actions';
import { switchOrganization } from './organization.actions';
import {
  filterSelectableOrganizations,
  getOrganizationSelectionStatus,
} from '../selectors/available-organizations.selector';
import { isOrganizationSelectorEnabled } from '../selectors/features.selector';
import { getUserId } from '../selectors/user.selector';

const logger = Logger.create({ loggerName: 'organizationSelection' });

const MIN_ORGANIZATIONS_FOR_SELECTOR = 2;

/** @returns {Promise<object[] | null>} the org list, or null when the fetch failed. */
export const fetchAvailableOrganizations = () => async dispatch => {
  let { data, error } = await organizationServiceApi.available();

  if (error) {
    return null;
  }

  dispatch({
    type: AVAILABLE_ORGANIZATIONS_UPDATE,
    payload: data,
  });

  return data?.organizations || [];
};

// "Last used" = the org the user last switched into. Written only by the
// ORGANIZATION_SWITCH middleware, never by the gate itself.
export const rememberSelectedOrganization =
  ({ organizationId }) =>
  (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);
    if (!userId || !organizationId) return;

    dispatch(
      updateDeviceFlags({
        key: DEVICE_FLAGS.SELECTED_ORGANIZATION_BY_USER,
        state: {
          ...(state.deviceFlags?.[DEVICE_FLAGS.SELECTED_ORGANIZATION_BY_USER] || {}),
          [userId]: organizationId,
        },
      }),
    );
  };

// RESOLVED releases the prompts held behind the gate. From IDLE there is
// nothing to release, and announcing would double-schedule every prompt that
// already fired on AUTH_LOGIN.
const markResolved = (dispatch, getState) => {
  if (getOrganizationSelectionStatus(getState()) !== ORGANIZATION_SELECTION_STATUS.IDLE) {
    dispatch({ type: ORGANIZATION_SELECTION_RESOLVED });
  }
  return false;
};

/**
 * Gates every full login of a multi-org user; a remembered org only feeds the
 * "last used" badge, it never skips the gate. RESOLVING is dispatched before
 * the first await so the other post-login prompts hold off in time. AUTH_LOGIN
 * only — resumed sessions keep the org their token carries.
 * @returns true when the selector is required.
 */
export const resolveOrganizationSelection = () => async (dispatch, getState) => {
  if (!isOrganizationSelectorEnabled(getState())) {
    // Keep the pre-flag behavior: login refreshes the list for the switch-org UI.
    dispatch(fetchAvailableOrganizations());
    return markResolved(dispatch, getState);
  }

  dispatch({ type: ORGANIZATION_SELECTION_RESOLVING });

  const organizations = await dispatch(fetchAvailableOrganizations());
  if (organizations === null) {
    // Never trap the user behind a gate we couldn't populate.
    logger.warn('available organizations fetch failed, skipping selector');
    return markResolved(dispatch, getState);
  }

  // The fetched list, not the slice — independent of when the reducer landed.
  const selectable = filterSelectableOrganizations(organizations);
  if (selectable.length < MIN_ORGANIZATIONS_FOR_SELECTOR) return markResolved(dispatch, getState);

  dispatch({ type: ORGANIZATION_SELECTION_REQUIRED });

  // A prompt that promoted itself while we were deciding goes back behind the gate.
  if (getState().auth?.suggestBiometricEnrollment) {
    dispatch(setSuggestBiometricEnrollment(false));
    dispatch(setPendingBiometricEnrollment(true));
  }

  return true;
};

// Applies the pick and releases the gate. Recording "last used" is left to the
// ORGANIZATION_SWITCH middleware that switchOrganization triggers.
export const selectOrganization =
  ({ id }) =>
  async (dispatch, getState) => {
    if (id !== getState().organization?.id) {
      const { error } = await dispatch(switchOrganization({ id }));
      if (error) return { error };
    }

    dispatch({ type: ORGANIZATION_SELECTION_RESOLVED });

    return {};
  };
